// GAVION training: levels, ranks, badges and mission states, derived from a TrainingStatus (my_training_status()
// for the signed-in rep, team_training_status() rows for admins, or the FakeRest twin in the demo).
import {
  MISSIONS,
  PRODUCT_MISSIONS,
  TEN_CALLS_TARGET,
  gameXp,
  type FieldMissionKey,
  type GameMission,
  type GameMissionKey,
  type Mission,
  type PlayableKey,
  type ProductTrack,
} from "./missions";

export type ProgressRow = {
  mission_key: PlayableKey;
  completed_at: string | null;
  best_score: number;
  attempts: number;
  xp: number;
  updated_at?: string | null;
};

/** Counts behind the field missions (SQL: private.training_status_for). */
export type FieldStats = {
  softphone_verified: boolean;
  sessions: number;
  /** Calls logged with one of the 8 dial outcomes. */
  logged_calls: number;
  /** Calls whose outcome reached a human (DIAL_OUTCOMES.reachedHuman). */
  reached_human: number;
  /** « Parlé – rappeler » calls with a callback date. */
  callbacks: number;
  /** « Démo réservée » calls plus the rep's deals with a demo date. */
  demos: number;
  /** Calls placed to a contact (calls.contact_id set). */
  contact_calls: number;
  /** Calls still « Non consigné » (the « À compléter » list). */
  unlogged: number;
};

export type TrainingStatus = {
  sales_id: number;
  first_name?: string | null;
  last_name?: string | null;
  progress: ProgressRow[];
  stats: FieldStats;
  missions: Record<FieldMissionKey, boolean>;
  last_activity: string | null;
};

export const EMPTY_STATS: FieldStats = Object.freeze({
  softphone_verified: false,
  sessions: 0,
  logged_calls: 0,
  reached_human: 0,
  callbacks: 0,
  demos: 0,
  contact_calls: 0,
  unlogged: 0,
});

/** Field mission flags from the counts. SQL twin: the `missions` object of private.training_status_for. */
export function fieldMissionsFrom(
  s: FieldStats,
): Record<FieldMissionKey, boolean> {
  return {
    ready: s.softphone_verified,
    first_session: s.sessions > 0,
    ten_calls: s.logged_calls >= TEN_CALLS_TARGET,
    first_contact: s.reached_human > 0,
    first_callback: s.callbacks > 0,
    first_demo: s.demos > 0,
    contact_call: s.contact_calls > 0,
    // nothing left to complete, and at least one call logged (an empty list on day one proves nothing)
    zero_unlogged: s.logged_calls > 0 && s.unlogged === 0,
  };
}

export type MissionState = "locked" | "available" | "in_progress" | "done";
export type MissionView = {
  mission: Mission;
  state: MissionState;
  xp: number;
  bestScore: number | null;
  attempts: number;
  /** Field missions: count toward the target (ten_calls 0–10, others 0–1). */
  count: number;
  target: number;
};

const fieldCount = (
  key: FieldMissionKey,
  s: FieldStats,
  done: boolean,
): number =>
  key === "ten_calls"
    ? Math.min(s.logged_calls, TEN_CALLS_TARGET)
    : done
      ? 1
      : 0;

const gameView = (
  mission: GameMission,
  row: ProgressRow | undefined,
  unlocked: boolean,
): MissionView => {
  const best = row?.best_score ?? 0;
  const done = !!row && best >= mission.pass;
  return {
    mission,
    state: done
      ? "done"
      : !unlocked
        ? "locked"
        : row && row.attempts > 0
          ? "in_progress"
          : "available",
    xp: gameXp(mission.key, best),
    bestScore: row ? best : null,
    attempts: row?.attempts ?? 0,
    count: done ? 1 : 0,
    target: 1,
  };
};

/**
 * One view per mission, in map order. A game mission unlocks when the game before it is done (the first one is
 * always open); field missions are never locked: they complete by themselves when the data shows them.
 */
export function missionViews(status: TrainingStatus): MissionView[] {
  const rows = new Map(status.progress.map((r) => [r.mission_key, r]));
  let previousGameDone = true;
  return MISSIONS.map((mission): MissionView => {
    if (mission.type === "field") {
      const done = !!status.missions[mission.key];
      const count = fieldCount(mission.key, status.stats, done);
      const partial =
        mission.key === "zero_unlogged" ? status.stats.unlogged > 0 : count > 0;
      return {
        mission,
        state: done ? "done" : partial ? "in_progress" : "available",
        xp: done ? mission.xp : 0,
        bestScore: null,
        attempts: 0,
        count,
        target: mission.target,
      };
    }
    const v = gameView(mission, rows.get(mission.key), previousGameDone);
    previousGameDone = v.state === "done";
    return v;
  });
}

/**
 * The « Produit » tab, in map order. « Vendre Gavion » unlocks one mission after the other (the first is open);
 * « Gavion à fond » opens when every « Vendre Gavion » mission is passed, then unlocks the same way.
 */
export function productMissionViews(status: TrainingStatus): MissionView[] {
  const rows = new Map(status.progress.map((r) => [r.mission_key, r]));
  const views: MissionView[] = [];
  let previousDone = true;
  for (const mission of PRODUCT_MISSIONS) {
    const firstDeep =
      mission.track === "deep" &&
      !views.some(
        (v) => v.mission.type === "game" && v.mission.track === "deep",
      );
    const sellDone = views
      .filter((v) => v.mission.type === "game" && v.mission.track === "sell")
      .every((v) => v.state === "done");
    const v = gameView(
      mission,
      rows.get(mission.key),
      firstDeep ? sellDone : previousDone,
    );
    previousDone = v.state === "done";
    views.push(v);
  }
  return views;
}

export const totalXp = (views: readonly MissionView[]): number =>
  views.reduce((s, v) => s + v.xp, 0);
export const doneCount = (views: readonly MissionView[]): number =>
  views.filter((v) => v.state === "done").length;

export const RANKS = [
  { key: "recruit", minXp: 0 },
  { key: "prospector", minXp: 300 },
  { key: "closer", minXp: 700 },
  { key: "pro", minXp: 1200 },
] as const;
export type RankKey = (typeof RANKS)[number]["key"];
export const XP_PER_LEVEL = 150;

export type Level = {
  level: number;
  /** XP earned inside the current level (0 … XP_PER_LEVEL - 1). */
  intoLevel: number;
  levelSize: number;
  rank: RankKey;
};

export function levelOf(xp: number): Level {
  const safe = Math.max(0, Math.floor(xp));
  const rank = [...RANKS].reverse().find((r) => safe >= r.minXp) ?? RANKS[0];
  return {
    level: 1 + Math.floor(safe / XP_PER_LEVEL),
    intoLevel: safe % XP_PER_LEVEL,
    levelSize: XP_PER_LEVEL,
    rank: rank.key,
  };
}

export const BADGE_KEYS = [
  "first_step",
  "flawless",
  "queue_master",
  "fire_fingers",
  "cool_head",
  "field_ready",
  "gavion_seller",
  "gavion_expert",
  "graduate",
] as const;
export type BadgeKey = (typeof BADGE_KEYS)[number];

/** « Doigts de feu » badge: a hotkey round at this score or better. */
export const FIRE_FINGERS_SCORE = 90;
const QUIZZES: readonly GameMissionKey[] = [
  "golden_hours",
  "right_outcome",
  "crisis",
];

const trackDone = (product: readonly MissionView[], track: ProductTrack) => {
  const t = product.filter(
    (v) => v.mission.type === "game" && v.mission.track === track,
  );
  return t.length > 0 && t.every((v) => v.state === "done");
};

export function earnedBadges(
  views: readonly MissionView[],
  product: readonly MissionView[] = [],
): BadgeKey[] {
  const best = (k: GameMissionKey) =>
    views.find((v) => v.mission.key === k)?.bestScore ?? 0;
  const fieldDone = views
    .filter((v) => v.mission.type === "field")
    .every((v) => v.state === "done");
  const earned: Record<BadgeKey, boolean> = {
    first_step:
      views.some((v) => v.state === "done") ||
      product.some((v) => v.state === "done"),
    flawless:
      QUIZZES.some((k) => best(k) >= 100) ||
      product.some((v) => (v.bestScore ?? 0) >= 100),
    queue_master: best("read_queue") >= 100,
    fire_fingers: best("hotkeys") >= FIRE_FINGERS_SCORE,
    cool_head: best("crisis") >= 100,
    field_ready: fieldDone,
    gavion_seller: trackDone(product, "sell"),
    gavion_expert: trackDone(product, "deep"),
    // the dialer course only
    graduate: views.every((v) => v.state === "done"),
  };
  return BADGE_KEYS.filter((k) => earned[k]);
}

/** A status with no data (new rep, or before the first load). */
export const emptyStatus = (salesId: number): TrainingStatus => ({
  sales_id: salesId,
  progress: [],
  stats: { ...EMPTY_STATS },
  missions: fieldMissionsFrom(EMPTY_STATS),
  last_activity: null,
});
